/** W47f: reine Funktionen fuer das Zusammenlegen von Absendern. Die Vorschau rechnet der Server (`probe`, W47b P7);
 *  hier werden nur ihre Werte in Saetze gesetzt (wortgleich in W47h iOS). */
import type { DoppelteAbsender, DoppelteGruppe, RegelKurz, ZusammenlegenAus } from "../api/typen";

export type VorschauNamen = {
  kategorie: (id: string) => string | undefined;
  person: (id: string) => string | undefined;
  /** Person nur nennen, wenn der Haushalt mehr als eine hat (wie der Satz der Pruefkarte) */
  mehrerePersonen: boolean;
};

const anfuehren = (name: string) => `„${name}“`;
const liste = new Intl.ListFormat("de-DE", { type: "conjunction" });
const FELDER: Record<string, string> = { email: "Mailadresse", fax: "Fax", kurzname: "Kurzname", kategorie_id: "Kategorie" };

function regelText(r: RegelKurz, namen: VorschauNamen): string {
  const kategorie = namen.kategorie(r.kategorie_id) ?? "eigene Kategorie";
  const person = r.person_id && namen.mehrerePersonen ? namen.person(r.person_id) : undefined;
  return person ? `${kategorie} für ${person}` : kategorie;
}

/** Saetze der Vorschau in der Reihenfolge der Tabelle im Auftrag W47f; der letzte ist immer „nicht rueckgaengig“. */
export function vorschauSaetze(aus: ZusammenlegenAus, namen: VorschauNamen): string[] {
  const q = anfuehren(aus.quelle.name);
  const z = anfuehren(aus.ziel.name);
  const s: string[] = [];
  const n = aus.dokumente;
  if (n === 0) s.push(`${q} hat keine Briefe.`);
  else if (n === 1) s.push(`1 Brief von ${q} geht zu ${z}.`);
  else s.push(`${n} Briefe von ${q} gehen zu ${z}.`);

  const akten = aus.akten.map((a) => a.name).sort((a, b) => a.localeCompare(b, "de"));
  const aktenListe = liste.format(akten.map(anfuehren));
  if (akten.length === 1) s.push(n === 1 ? `Der Brief bleibt in der Akte ${aktenListe}.` : `Die Briefe bleiben in der Akte ${aktenListe}.`);
  else if (akten.length > 1) s.push(n === 1 ? `Der Brief bleibt in seinen Akten ${aktenListe}.` : `Die Briefe bleiben in ihren Akten ${aktenListe}.`);

  if (aus.regel === "uebernommen" && aus.regel_quelle) {
    s.push(`Die Regel von ${q} (${regelText(aus.regel_quelle, namen)}) gilt danach für ${z}.`);
  } else if (aus.regel === "zusammengezaehlt" && (aus.regel_ziel ?? aus.regel_quelle)) {
    s.push(`Beide haben dieselbe Regel (${regelText((aus.regel_ziel ?? aus.regel_quelle) as RegelKurz, namen)}); sie gilt danach einmal für ${z}.`);
  } else if (aus.regel === "ziel_bleibt" && aus.regel_quelle) {
    const weiter = aus.regel_ziel ? ` Für ${z} gilt weiter ${regelText(aus.regel_ziel, namen)}.` : "";
    s.push(`Die Regel von ${q} (${regelText(aus.regel_quelle, namen)}) entfällt.${weiter}`);
  }

  if (aus.entschieden === 1) s.push(`Deine Entscheidung zu einem dieser Briefe zählt danach für ${z}.`);
  else if (aus.entschieden > 1) s.push(`Deine Entscheidungen zu ${aus.entschieden} dieser Briefe zählen danach für ${z}.`);

  if (aus.vergessen === 1) s.push("Ein Brief, den Gaist vergessen sollte, bleibt vergessen.");
  else if (aus.vergessen > 1) s.push(`${aus.vergessen} Briefe, die Gaist vergessen sollte, bleiben vergessen.`);

  const sw = aus.schreibweisen.map(anfuehren);
  if (sw.length === 1) {
    s.push(`Gaist merkt sich ${sw[0]} als Schreibweise von ${z}. Briefe mit diesem Namen kommen künftig zu ${z}, auch in leicht anderer Schreibweise.`);
  } else if (sw.length > 1) {
    s.push(
      `Gaist merkt sich ${liste.format(sw)} als Schreibweisen von ${z}. Briefe mit diesen Namen kommen künftig zu ${z}, auch in leicht anderer Schreibweise.`,
    );
  }

  const felder = aus.felder.map((f) => FELDER[f]).filter((f): f is string => Boolean(f));
  if (felder.length) s.push(`${z} übernimmt von ${q}: ${felder.join(", ")}.`);

  s.push("Das lässt sich nicht rückgängig machen.");
  return s;
}

/** IDs der Glieder sortiert und mit Komma verbunden (keine Namen). */
export function gruppenSchluessel(g: DoppelteGruppe): string {
  return g.absender
    .map((a) => a.id)
    .sort()
    .join(",");
}

function istGruppe(g: unknown): g is DoppelteGruppe {
  const x = g as DoppelteGruppe | null;
  return Boolean(x && typeof x.grund === "string" && typeof x.vorschlag_ziel === "string" && Array.isArray(x.absender) && x.absender.length >= 2);
}

/** Antwort von `GET /absender/doppelte`: heute eine Liste der Gruppen; mit W47b-2 `{gruppen, ausgeblendet}`. Was
 *  nicht passt, gibt keine Vorschlaege (ein Vorschlag darf das Archiv nie stoeren, F-W1). */
export function doppelteLesen(roh: unknown): DoppelteAbsender {
  if (Array.isArray(roh)) return { gruppen: roh.filter(istGruppe), ausgeblendet: 0 };
  const o = roh as { gruppen?: unknown; ausgeblendet?: unknown } | null;
  if (o && typeof o === "object" && Array.isArray(o.gruppen)) {
    const zahl = typeof o.ausgeblendet === "number" && o.ausgeblendet > 0 ? Math.floor(o.ausgeblendet) : 0;
    return { gruppen: o.gruppen.filter(istGruppe), ausgeblendet: zahl };
  }
  return { gruppen: [], ausgeblendet: 0 };
}

/** Grund des Servers mit grossem Anfangsbuchstaben („Gleicher Name bis auf die Schreibweise“). */
export function grundText(grund: string): string {
  return grund.charAt(0).toUpperCase() + grund.slice(1);
}

export const GRUND_OHNE_RECHTSFORM = "gleicher Name, einmal ohne Rechtsform";

/** Name des Glieds ohne Rechtsform (der kuerzere, bei Gleichstand der erste nach Name); nur bei diesem Grund. */
export function ohneRechtsform(g: DoppelteGruppe): string | undefined {
  if (g.grund !== GRUND_OHNE_RECHTSFORM) return undefined;
  return [...g.absender].sort((a, b) => a.name.length - b.name.length || a.name.localeCompare(b.name, "de"))[0]?.name;
}
